import React from 'react';
import { useHistory, useLocation } from 'react-router';
import { ButtonProps } from '@naf/button';
import { useAuth0 } from '@auth0/auth0-react';
import { StyledButton } from './ProductArticleButton.styles';
import { ProductArticleType } from '../../../../../types/articleType';
import { useGTMDataLayer } from '../../../hooks/GTM/useGTMDataLayer';
import { EventPayload } from '../../../../../types/GTM/EventPayload';
import { isInternalLink } from '../../../lib/isInternalLink';
import { getHostname } from '../../../lib/getHostname';
import useSelector from '../../../redux/typedHooks';
import { MEMBERPAGES_URL } from '../../../lib/constants';

type Props = {
  productArticle: ProductArticleType;
  url: string;
  text: string;
} & Pick<ButtonProps, 'variant'>;

export const ProductArticleButton = ({ url, text, variant, productArticle }: Props) => {
  const history = useHistory();
  const baseUrl = useSelector((state) => state.application.baseUrl);
  const location = useLocation();
  // Onload Tracking
  const dataLayer = useGTMDataLayer();
  const { loginWithRedirect, isAuthenticated } = useAuth0();

  const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    // Handle ecommerce tracking events
    if (productArticle.relatedProduct?.ctaButtonLink?.tracking?.event) {
      const payload: EventPayload = { event: productArticle.relatedProduct?.ctaButtonLink?.tracking?.event };
      if (
        productArticle.relatedProduct?.ctaButtonLink?.tracking?.payloadReference === 'ecommerce' &&
        productArticle.ecommerce
      ) {
        payload.ecommerce = productArticle.ecommerce;
      }
      dataLayer?.push(payload);
    }

    // Handle external / internal links and click tracking
    if (url) {
      const { isInternal, relativeLink } = isInternalLink(baseUrl, url);
      if (isInternal) {
        const isLoginButton = relativeLink.startsWith(`/${MEMBERPAGES_URL}`);

        dataLayer?.push({
          event: 'cta_click',
          cta_text: text,
          cta_location: location.pathname,
          cta_url: url,
          cta_hostname: getHostname(url),
          cta_position: 'content',
        });
        if (isLoginButton && !isAuthenticated) {
          await loginWithRedirect({ appState: { returnTo: relativeLink } });
        } else {
          history.push(relativeLink);
        }
      } else {
        const linkUrlObject = (url && new URL(url)) || undefined;
        dataLayer?.push({
          event: 'cta_click',
          cta_text: text,
          cta_location: location.pathname,
          cta_url: url,
          cta_hostname: linkUrlObject?.hostname,
          cta_position: 'content',
        });

        window.open(url);
      }
    }
  };

  return (
    <StyledButton href={url} onClick={handleClick} variant={variant}>
      {text}
    </StyledButton>
  );
};
